import * as XLSX from 'xlsx'

const EXCEL_SHEET_NAME_MAX = 31

function sanitizeSheetName(name) {
  const cleaned = String(name).replace(/[\\/*?:[\]]/g, '_')
  return cleaned.slice(0, EXCEL_SHEET_NAME_MAX)
}

export function serializeExportValue(value) {
  if (value === null || value === undefined) return ''
  if (typeof value === 'object') return JSON.stringify(value)
  return String(value)
}

function objectToFieldValueSheet(obj) {
  if (!obj || typeof obj !== 'object' || Array.isArray(obj)) {
    return XLSX.utils.aoa_to_sheet([['Field', 'Value'], ['(empty)', '']])
  }
  const rows = Object.entries(obj).map(([key, value]) => ({
    Field: key,
    Value: serializeExportValue(value)
  }))
  return XLSX.utils.json_to_sheet(rows)
}

function appendSheet(workbook, sheet, name) {
  let sheetName = sanitizeSheetName(name)
  let suffix = 1
  while (workbook.SheetNames.includes(sheetName)) {
    sheetName = sanitizeSheetName(`${name} ${suffix++}`)
  }
  XLSX.utils.book_append_sheet(workbook, sheet, sheetName)
}

function columnListSheet(columns) {
  const rows = (columns || []).map((col, index) => ({
    Index: index + 1,
    Column: col
  }))
  return XLSX.utils.json_to_sheet(rows)
}

function hasContent(value) {
  if (value == null) return false
  if (Array.isArray(value)) return value.length > 0
  if (typeof value === 'object') return Object.keys(value).length > 0
  return true
}

/**
 * Build a multi-sheet workbook with processing summary and related metadata.
 */
export function buildProcessingSummaryWorkbook(processingResults = {}) {
  const workbook = XLSX.utils.book_new()

  if (hasContent(processingResults.processing_summary)) {
    appendSheet(
      workbook,
      objectToFieldValueSheet(processingResults.processing_summary),
      'Processing Summary'
    )
  }

  const runInfo = {
    subid: processingResults.subid,
    dataset_identifier: processingResults.dataset_identifier,
    session_id: processingResults.session_id,
    success: processingResults.success,
    steps_completed: processingResults.steps_completed,
    errors: processingResults.errors ?? processingResults.processing_errors,
    warnings: processingResults.warnings,
    results_stored_in_s3: processingResults.results_stored_in_s3,
    s3_key: processingResults.s3_key
  }
  const runInfoFiltered = Object.fromEntries(
    Object.entries(runInfo).filter(([, value]) => hasContent(value))
  )
  if (Object.keys(runInfoFiltered).length > 0) {
    appendSheet(workbook, objectToFieldValueSheet(runInfoFiltered), 'Run Info')
  }

  const columnMetadata = processingResults.column_metadata
  if (columnMetadata && typeof columnMetadata === 'object') {
    const {
      day_level_feature_registry: registry,
      curve_features_order: curveOrder,
      day_level_data_order: dayOrder,
      ...scalarMeta
    } = columnMetadata

    const metaOverview = { ...scalarMeta }
    if (curveOrder?.length) metaOverview.curve_features_column_count = curveOrder.length
    if (dayOrder?.length) metaOverview.day_level_data_column_count = dayOrder.length
    if (registry?.length) metaOverview.day_level_feature_registry_count = registry.length

    appendSheet(workbook, objectToFieldValueSheet(metaOverview), 'Column Metadata')

    if (curveOrder?.length) {
      appendSheet(workbook, columnListSheet(curveOrder), 'Curve Column Order')
    }
    if (dayOrder?.length) {
      appendSheet(workbook, columnListSheet(dayOrder), 'Day Column Order')
    }
    if (registry?.length) {
      appendSheet(workbook, XLSX.utils.json_to_sheet(registry), 'Day Export Registry')
    }
  }

  if (hasContent(processingResults.adherence_review)) {
    appendSheet(
      workbook,
      objectToFieldValueSheet(processingResults.adherence_review),
      'Adherence Review'
    )
  }

  if (hasContent(processingResults.drinking_prediction_review)) {
    appendSheet(
      workbook,
      objectToFieldValueSheet(processingResults.drinking_prediction_review),
      'Drinking Pred Review'
    )
  }

  const flagSelections = processingResults.flag_selections
    ?? processingResults.processing_summary?.settings_used?.curve_attrs?.flag_selections
  if (hasContent(flagSelections)) {
    appendSheet(workbook, objectToFieldValueSheet(flagSelections), 'Flag Selections')
  }

  if (hasContent(processingResults.drinking_day_score_meta)) {
    appendSheet(
      workbook,
      objectToFieldValueSheet(processingResults.drinking_day_score_meta),
      'ML Score Meta'
    )
  }

  if (hasContent(processingResults.data_metadata)) {
    appendSheet(workbook, objectToFieldValueSheet(processingResults.data_metadata), 'Data Metadata')
  }

  if (workbook.SheetNames.length === 0) {
    appendSheet(
      workbook,
      objectToFieldValueSheet({ message: 'No metadata available' }),
      'Processing Summary'
    )
  }

  return workbook
}

/**
 * Cohort export: overview plus per-subject summary rows.
 */
export function buildCohortProcessingSummaryWorkbook(processingResults = {}, columnMetadata = {}) {
  const workbook = XLSX.utils.book_new()

  if (hasContent(processingResults.summary_stats)) {
    appendSheet(
      workbook,
      objectToFieldValueSheet(processingResults.summary_stats),
      'Cohort Overview'
    )
  }

  const subjects = processingResults.processed_subjects || []
  if (subjects.length > 0) {
    const subjectRows = subjects.map((subject) => ({
      subid: subject.subid,
      processing_status: subject.processing_status,
      curves: subject.curve_features_count ?? subject.processing_summary?.curves_identified ?? '',
      days: subject.day_level_data_count ?? subject.processing_summary?.days_analyzed ?? '',
      rows: subject.summary_stats?.total_rows ?? subject.processing_summary?.configured_rows ?? '',
      duration_hours: subject.summary_stats?.data_duration_hours ?? '',
      unique_days: subject.summary_stats?.unique_days ?? ''
    }))
    appendSheet(workbook, XLSX.utils.json_to_sheet(subjectRows), 'Subjects')

    const detailRows = []
    for (const subject of subjects) {
      const summary = subject.processing_summary || {}
      for (const [field, value] of Object.entries(summary)) {
        if (field === 'settings_used') continue
        detailRows.push({
          subid: subject.subid,
          field,
          value: serializeExportValue(value)
        })
      }
    }
    if (detailRows.length > 0) {
      appendSheet(workbook, XLSX.utils.json_to_sheet(detailRows), 'Subject Summaries')
    }

    const settingsRows = subjects.flatMap((subject) => {
      const settings = subject.processing_summary?.settings_used
      if (!settings) return []
      return [{
        subid: subject.subid,
        settings: serializeExportValue(settings)
      }]
    })
    if (settingsRows.length > 0) {
      appendSheet(workbook, XLSX.utils.json_to_sheet(settingsRows), 'Subject Settings')
    }
  }

  const failedSubjects = processingResults.failed_subjects || []
  if (failedSubjects.length > 0) {
    appendSheet(workbook, XLSX.utils.json_to_sheet(failedSubjects), 'Failed Subjects')
  }

  if (hasContent(columnMetadata)) {
    appendSheet(workbook, objectToFieldValueSheet(columnMetadata), 'Column Metadata')
    if (columnMetadata.curve_features_order?.length) {
      appendSheet(workbook, columnListSheet(columnMetadata.curve_features_order), 'Curve Column Order')
    }
    if (columnMetadata.day_level_data_order?.length) {
      appendSheet(workbook, columnListSheet(columnMetadata.day_level_data_order), 'Day Column Order')
    }
  }

  if (workbook.SheetNames.length === 0) {
    appendSheet(
      workbook,
      objectToFieldValueSheet({ message: 'No cohort metadata available' }),
      'Cohort Overview'
    )
  }

  return workbook
}

export function processingSummaryFilename(subid, datasetId = '001') {
  return `${subid}_${datasetId}_processing_summary.xlsx`
}

export function cohortProcessingSummaryFilename() {
  return `cohort_${new Date().toISOString().split('T')[0]}_processing_summary.xlsx`
}

export function downloadProcessingSummaryWorkbook(workbook, filename) {
  XLSX.writeFile(workbook, filename)
  return { success: true, filename }
}

export function downloadSubjectProcessingSummary(processingResults, subid, datasetId = '001') {
  const workbook = buildProcessingSummaryWorkbook(processingResults)
  const filename = processingSummaryFilename(subid, datasetId)
  return downloadProcessingSummaryWorkbook(workbook, filename)
}

/**
 * Payload for server-side zip download processing summary sheet.
 */
export function buildProcessingSummaryPayload(processedData = {}, identifiers = {}) {
  return {
    subid: identifiers.subid ?? processedData.subid,
    dataset_identifier: identifiers.dataset_identifier ?? processedData.dataset_identifier,
    session_id: processedData.session_id,
    success: processedData.success,
    processing_summary: processedData.processing_summary || {},
    column_metadata: processedData.column_metadata || null,
    adherence_review: processedData.adherence_review,
    drinking_prediction_review: processedData.drinking_prediction_review,
    flag_selections: processedData.flag_selections,
    steps_completed: processedData.steps_completed,
    errors: processedData.errors ?? processedData.processing_errors,
    warnings: processedData.warnings,
    drinking_day_score_meta: processedData.drinking_day_score_meta,
    data_metadata: processedData.data_metadata,
    results_stored_in_s3: processedData.results_stored_in_s3,
    s3_key: processedData.s3_key
  }
}

export function downloadCohortProcessingSummary(processingResults, columnMetadata = {}) {
  const workbook = buildCohortProcessingSummaryWorkbook(processingResults, columnMetadata)
  const filename = cohortProcessingSummaryFilename()
  return downloadProcessingSummaryWorkbook(workbook, filename)
}
